import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, Clock, CheckCircle, XCircle, IndianRupee, TrendingUp, FlaskConical, ExternalLink, ArrowRight, AlertCircle, Calendar, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  under_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  needs_revision: 'bg-orange-100 text-orange-700',
};

function FormCard({ form }) {
  // Route to appropriate dashboard based on form type/name
  const nameLower = form.name?.toLowerCase() || '';
  const isManagementAuditForm = nameLower.includes('management level') || nameLower.includes('management audit') || form.form_type === 'management';
  const isTargetPlanForm = nameLower.includes('target plan') || form.form_type === 'target_plan';
  const isCareerAdvisorForm = !isManagementAuditForm && !isTargetPlanForm && (
    nameLower.includes('career advisor') || nameLower.includes('career counsellor') ||
    nameLower.includes('retail sales') || nameLower.includes('sales achieved') ||
    nameLower.includes('sales monthly') || nameLower.includes('monthly sales') ||
    form.form_type === 'monthly_sales'
  );
  const isIncentiveForm = nameLower.includes('incentive') || nameLower.includes('payout') || form.form_type === 'expense';
  
  let dashboardUrl = `/form-dashboard/${form.id}`;
  if (isManagementAuditForm) {
    dashboardUrl = `/management-audit-dashboard/${form.id}`;
  } else if (isTargetPlanForm) {
    dashboardUrl = `/target-plan-dashboard/${form.id}`;
  } else if (isIncentiveForm) {
    dashboardUrl = `/incentive-dashboard`;
  } else if (isCareerAdvisorForm) {
    dashboardUrl = `/career-advisor-dashboard/${form.id}`;
  }
  
  const openDashboard = () => window.open(dashboardUrl, '_blank');
  const hasData = form.stats.total > 0;

  return (
    <Card
      data-testid={`form-card-${form.id}`}
      className="border border-border rounded-sm shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
      onClick={openDashboard}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-sm font-semibold truncate group-hover:text-blue-600 transition-colors">{form.name}</h3>
            <div className="flex items-center gap-1.5 mt-1">
              <Badge variant="outline" className="text-[9px] rounded-sm px-1.5 py-0 h-4">{form.form_type}</Badge>
              <Badge className={`text-[9px] rounded-sm px-1.5 py-0 h-4 ${form.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{form.status}</Badge>
            </div>
          </div>
          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-blue-600 transition-colors flex-shrink-0 mt-0.5" />
        </div>

        {hasData ? (
          <div className="space-y-2.5 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Submissions</span>
              <span className="font-data text-sm font-bold">{form.stats.total.toLocaleString('en-IN')}</span>
            </div>
            {form.stats.total_amount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Amount</span>
                <span className="font-data text-sm font-bold text-emerald-700">₹{form.stats.total_amount.toLocaleString('en-IN')}</span>
              </div>
            )}
            {form.stats.pending > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Pending</span>
                <span className="font-data text-sm font-semibold text-amber-700">{form.stats.pending}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-4 py-2">
            {!form.has_mapping ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-amber-500" /> Set up mapping to see analytics
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">No submissions yet</p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          {form.updated_at ? (
            <span className="font-data text-[10px] text-muted-foreground">
              Last: {formatDistanceToNow(new Date(form.updated_at), { addSuffix: true })}
            </span>
          ) : (
            <span className="font-data text-[10px] text-muted-foreground">Never received</span>
          )}
          <span className="text-[10px] font-medium text-blue-600 flex items-center gap-0.5 group-hover:gap-1.5 transition-all">
            Open <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [formCards, setFormCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTest, setShowTest] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/dashboard/overview', { params: { show_test: showTest } }),
      api.get('/form-dashboard/cards', { params: { show_test: showTest } })
    ]).then(([overviewRes, cardsRes]) => {
      setData(overviewRes.data);
      setFormCards(cardsRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [showTest]);

  if (loading) return <div className="flex items-center justify-center h-64"><p className="font-data text-sm text-muted-foreground">Loading dashboard...</p></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Failed to load dashboard data</div>;

  const { kpis, recent_submissions, recent_activity } = data;
  const kpiCards = [
    { label: 'TOTAL SUBMISSIONS', value: kpis.total_submissions, icon: FileText, color: 'text-[#0f172a]' },
    { label: 'PENDING REVIEW', value: kpis.pending, icon: Clock, color: 'text-amber-600' },
    { label: 'APPROVED', value: kpis.approved, icon: CheckCircle, color: 'text-emerald-600' },
    { label: 'TOTAL AMOUNT', value: `₹${kpis.total_amount?.toLocaleString('en-IN') || 0}`, icon: IndianRupee, color: 'text-blue-600' },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Sales & expense data at a glance</p>
        </div>
        <Button
          variant={showTest ? 'default' : 'outline'}
          size="sm"
          className={`rounded-sm text-xs gap-1.5 ${showTest ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' : 'text-muted-foreground'}`}
          onClick={() => setShowTest(v => !v)}
          data-testid="dashboard-show-test-toggle"
        >
          <FlaskConical className="w-3.5 h-3.5" />
          {showTest ? 'Incl. Test Data' : 'Excl. Test Data'}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-cards">
        {kpiCards.map((kpi, i) => (
          <Card key={i} className="border border-border rounded-sm shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{kpi.label}</span>
                <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
              </div>
              <p className="font-data text-2xl font-bold tracking-tight">{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Weekly Plan & Attendance */}
      <Card
        className="mb-8 border border-slate-200 bg-gradient-to-r from-indigo-50 via-violet-50 to-white hover:shadow-lg transition-all cursor-pointer"
        onClick={() => navigate('/weekly-plan')}
        data-testid="weekly-plan-tile"
      >
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-center flex-shrink-0 shadow">
            <Calendar className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-base font-bold text-slate-800 mb-1">Weekly Plan & Attendance</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Plan each user's week with location, activity & hours.
              Managers get a quick "Plan Next Week" flow and bulk-fill rows in one click.
            </p>
          </div>
          <div className="flex items-center gap-1 text-violet-700 font-semibold text-sm flex-shrink-0">
            Open <ArrowRight className="w-4 h-4" />
          </div>
        </CardContent>
      </Card>

      {/* Form Dashboards Grid */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Form Dashboards</h2>
          <span className="text-xs text-muted-foreground">{formCards.length} form{formCards.length !== 1 ? 's' : ''} · click to open in new tab</span>
        </div>
        {formCards.length === 0 ? (
          <Card className="border border-dashed border-border rounded-sm">
            <CardContent className="flex flex-col items-center justify-center h-32">
              <p className="text-sm text-muted-foreground">No forms registered yet</p>
              <p className="text-xs text-muted-foreground mt-1">Go to Webhooks → Register Form to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {formCards.map(form => <FormCard key={form.id} form={form} />)}
          </div>
        )}
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Submissions */}
        <Card className="border border-border rounded-sm shadow-sm lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base font-semibold">Recent Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            {recent_submissions.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase tracking-wider">Form</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Submitter</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Amount</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent_submissions.map(s => (
                    <TableRow key={s.id} className="cursor-pointer hover:bg-muted/50" onClick={() => window.location.href = `/submissions/${s.id}`}>
                      <TableCell className="font-medium text-sm">{s.form_name}</TableCell>
                      <TableCell className="text-sm">{s.submitted_by_name}</TableCell>
                      <TableCell className="font-data text-sm">{s.mapped_data?.amount ? `₹${Number(s.mapped_data.amount).toLocaleString('en-IN')}` : '--'}</TableCell>
                      <TableCell><Badge variant="secondary" className={`text-[10px] rounded-sm ${statusColors[s.status] || ''}`}>{s.status}</Badge></TableCell>
                      <TableCell className="font-data text-xs text-muted-foreground">{formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">No submissions yet</p>
            )}
          </CardContent>
        </Card>

        {/* Activity Feed */}
        <Card className="border border-border rounded-sm shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base font-semibold">Activity Feed</CardTitle>
          </CardHeader>
          <CardContent>
            {recent_activity.length > 0 ? (
              <div className="space-y-3">
                {recent_activity.map(a => (
                  <div key={a.id} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm leading-snug">{a.details}</p>
                      <p className="font-data text-[10px] text-muted-foreground mt-0.5">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">No activity yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
